import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ToastProvider } from '@topolo/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { getOfflineHandler, registerOfflineTranslator } from './offline-handler';
import {
  TopoloAppErrorBoundary,
  TopoloAppRoot,
  createTopoloLazyComponent,
  createTopoloLazyRoute,
  useAuth,
  getAppId,
  getAppName,
  type AppRoute,
} from '@topolo-io/app-shell';
import {
  LandingPage as SharedLandingPage,
  LoginPage,
  TopoloAuthCallback,
  TopoloAppLoadingScaffold,
  useAuthenticatedHomePath,
} from '@topolo-io/app-shell';
import { ConfirmDialogHost, confirmDialog, TopoloI18nProvider, topoloServiceUrl, setupBugReporterErrorCapture, TopoloContentSkeleton, useTranslation, useLocale, type TopoloContentSkeletonVariant } from '@topolo-io/ui-kit';
import { authClient } from './lib/topolo-auth';
import { api } from './lib/api';
import { ROADMAPPER_WORKSPACE_STORAGE_KEY } from './lib/workspace';
import { WorkspaceRouteBoundary } from './components/workspace-route-boundary';
import { GuestProjectSkeleton } from './components/guest-project-skeleton';

// Wire global error/console capture so BugReporter (enabled in TopoloAppShell)
// can attach the last few errors to any submitted bug report.
setupBugReporterErrorCapture();

// Identity is resolved at runtime by the shared worker-runtime (from the
// Developers Catalog, by host) and read synchronously from the injected globals.
const APP_NAME = getAppName();
const HOME_PATH = '/dashboard';

// Both translation providers (this root one and the AppShell's) read the
// immutable release from UI Kit's per-app, per-revision default path,
// /topolo-i18n-release/<appId>/<revision>.json, bound to the runtime app id and
// the compiled revision. The deploy publishes the release there.
function roadmapperTranslationSource() {
  return { revision: import.meta.env.VITE_TOPOLO_REVISION, catalogManifestUrl: topoloServiceUrl('localize', '/api/catalog-manifest') };
}

/** AppShell translation config: the same release, following the root provider's committed locale. */
export function useRoadmapperI18n() {
  const { locale, setLocale } = useLocale();
  return { ...roadmapperTranslationSource(), locale, onLocaleChange: setLocale, loadingFallback: <ShellScaffold /> };
}

export type RoadmapperServiceWorkerRegistrar = (options: { onNeedRefresh: () => Promise<void>; onOfflineReady: () => void }) => (reloadPage?: boolean) => Promise<void>;

export function RoadmapperPWAUpdate({ registerServiceWorker }: { registerServiceWorker: RoadmapperServiceWorkerRegistrar }) {
  const { t } = useTranslation();
  const currentTranslation = useRef(t);
  const registered = useRef<RoadmapperServiceWorkerRegistrar | null>(null);
  useLayoutEffect(() => { currentTranslation.current = t; }, [t]);
  useEffect(() => {
    if (registered.current === registerServiceWorker) return;
    registered.current = registerServiceWorker;
    const updateSW = registerServiceWorker({
      async onNeedRefresh() {
        const t = currentTranslation.current;
        if (await confirmDialog({ title: t('roadmapper.main.reloadRoadmapper'), message: t('roadmapper.main.newContentReloadNow'), confirmLabel: t('roadmapper.main.reload'), confirmVariant: 'primary' })) await updateSW(true);
      },
      onOfflineReady() {},
    });
  }, [registerServiceWorker]);
  return null;
}

function RoadmapperOfflineTranslationBridge() {
  const { t } = useTranslation();
  const current = useRef(t);
  useLayoutEffect(() => { current.current = t; }, [t]);
  useLayoutEffect(() => registerOfflineTranslator((key, options) => current.current(key, options)), []);
  return null;
}

export function RoadmapperRuntime({ registerServiceWorker, children }: { registerServiceWorker?: RoadmapperServiceWorkerRegistrar; children?: ReactNode }) {
  return <TopoloI18nProvider appId={getAppId()} {...roadmapperTranslationSource()} locale="en" defaultLocale="en" getAccessToken={() => authClient.getAccessToken()}>
    <RoadmapperOfflineTranslationBridge />
    {children ?? <App />}
    {registerServiceWorker && <RoadmapperPWAUpdate registerServiceWorker={registerServiceWorker} />}
  </TopoloI18nProvider>;
}

// React Query client lives in lib/queryClient.ts (fleet convention: cached,
// refetchOnMount:false). Cleared on auth-clear / org-change below.

/**
 * Lazy route + chunk prefetch in one declaration.
 *
 * TopoloAppRoot preloads any route that declares `preload` as soon as the user
 * is authenticated, so the chunk is already in memory by the time it is
 * navigated to and its fallback skeleton never paints. Roadmapper previously
 * declared no preloads, so every first visit to a section showed a chunk
 * skeleton and then the page's own data skeleton — two scaffolds back to back.
 *
 * `loader` is shared between the two so they can never point at different
 * modules.
 */
function lazyRoute(
  loader: () => Promise<{ default: React.ComponentType<Record<string, never>> }>,
  variant: TopoloContentSkeletonVariant,
  // A page whose own loading state is bespoke passes it here, so the chunk
  // fallback and the data skeleton are the same shape. Without this the reader
  // sees three shapes in a row: generic skeleton, page skeleton, page.
  fallback?: React.ReactElement,
) {
  return {
    ...createTopoloLazyRoute({
      loader,
      fallback: fallback ?? <RouteFallback variant={variant} />,
    }),
    preload: loader,
  };
}

// In-shell content skeleton for a route chunk. The shell (sidebar + topbar) is
// already mounted around <Outlet/>, so this must be a CONTENT-level skeleton —
// a full TopoloAppLoadingScaffold here paints a second fake shell inside the
// real one. The variant must match the page's own data skeleton, or the
// skeleton visibly changes shape when the page takes over.
/** Shell-shaped scaffold: the frame shown before any chrome is mounted. */
function ShellScaffold() {
  return <TopoloAppLoadingScaffold appName={APP_NAME} appId={getAppId()} />;
}

function RouteFallback({ variant = 'dashboard' }: { variant?: TopoloContentSkeletonVariant }) {
  return <TopoloContentSkeleton variant={variant} />;
}

// The shell stays code-split so the PUBLIC landing bundle doesn't carry it
// (app-contract.test.ts pins this). But its fallback must be SHELL-shaped: this
// renders before any chrome exists, so a content-level skeleton here made the
// sidebar and topbar visibly vanish and come back between the auth scaffold and
// the real shell. TopoloAppLoadingScaffold is the same scaffold the auth phase
// shows, so the two phases now read as one continuous frame.
const LazyAppShell = createTopoloLazyComponent<{ children: ReactNode }>({
  loader: () => import('./components/app-shell').then((m) => ({ default: m.AppShell })),
  fallback: <ShellScaffold />,
}).Component;

// PWA install prompt + offline indicator are non-visual chrome overlays; a
// skeleton would flash a fake panel, so they load with no fallback.
const PWAInstallBanner = createTopoloLazyComponent({
  loader: () => import('./components/pwa-install-banner').then((m) => ({ default: m.PWAInstallBanner })),
  fallback: null,
}).Component;
const OfflineStatus = createTopoloLazyComponent({
  loader: () => import('./components/offline-status').then((m) => ({ default: m.OfflineStatus })),
  fallback: null,
}).Component;
const DashboardRoute = lazyRoute(
  () => import('./pages/dashboard').then((m) => ({ default: m.Dashboard })),
  'dashboard',
);
const CalendarRoute = lazyRoute(
  () => import('./pages/calendar').then((m) => ({ default: m.Calendar })),
  'calendar',
);
const ProjectsRoute = lazyRoute(
  () => import('./pages/projects').then((m) => ({ default: m.Projects })),
  'card-grid',
);
const NewProjectRoute = lazyRoute(
  () => import('./pages/projects/new').then((m) => ({ default: m.NewProject })),
  'form',
);
const NewProjectGalleryRoute = lazyRoute(
  () => import('./pages/projects/gallery').then((m) => ({ default: m.NewProjectGallery })),
  'card-grid',
);
const ProjectDetailRoute = lazyRoute(
  () => import('./pages/projects/[id]').then((m) => ({ default: m.ProjectDetail })),
  'detail',
);
const ProjectDashboardRoute = lazyRoute(
  () => import('./pages/projects/dashboard').then((m) => ({ default: m.ProjectDashboard })),
  'dashboard',
);
const GuestProjectViewRoute = lazyRoute(
  () => import('./pages/projects/guest/[token]').then((m) => ({ default: m.GuestProjectView })),
  'detail',
  // The public share view renders outside the shell, so a content-level
  // skeleton stood alone on an empty page and looked nothing like what
  // arrived. This is the same skeleton the page shows while its data loads.
  <GuestProjectSkeleton />,
);
const RoadmapsRoute = lazyRoute(
  () => import('./pages/roadmaps').then((m) => ({ default: m.Roadmaps })),
  'card-grid',
);
const SharedRoadmapsRoute = lazyRoute(
  () => import('./pages/shared-roadmaps').then((m) => ({ default: m.SharedRoadmaps })),
  'card-grid',
);
const NewRoadmapRoute = lazyRoute(
  () => import('./pages/roadmaps/new').then((m) => ({ default: m.NewRoadmap })),
  'form',
);
const RoadmapDetailRoute = lazyRoute(
  () => import('./pages/roadmaps/[id]').then((m) => ({ default: m.RoadmapDetail })),
  'board',
);
const RoadmapItemDetailRoute = lazyRoute(
  () => import('./pages/roadmap-items/[id]').then((m) => ({ default: m.RoadmapItemDetail })),
  'detail',
);
const GuestRoadmapItemViewRoute = lazyRoute(
  () => import('./pages/roadmap-items/guest/[token]').then((m) => ({ default: m.GuestRoadmapItemView })),
  'detail',
);
const GuestRoadmapViewRoute = lazyRoute(
  () => import('./pages/roadmaps/guest/[token]').then((m) => ({ default: m.GuestRoadmapView })),
  'board',
);
const PresentationDetailRoute = lazyRoute(
  () => import('./pages/presentations/[id]').then((m) => ({ default: m.PresentationDetail })),
  'detail',
);
const SettingsRoute = lazyRoute(
  () => import('./pages/settings').then((m) => ({ default: m.Settings })),
  'form',
);
const ImportPageRoute = lazyRoute(
  () => import('./pages/import').then((m) => ({ default: m.ImportPage })),
  'form',
);
const ExportPageRoute = lazyRoute(
  () => import('./pages/export').then((m) => ({ default: m.ExportPage })),
  'form',
);
const ReportsRoute = lazyRoute(
  () => import('./pages/reports'),
  'dashboard',
);
const GettingStartedPageRoute = lazyRoute(
  () => import('./pages/GettingStartedPage').then((m) => ({ default: m.GettingStartedPage })),
  'actions',
);

// Protected product routes — gated + wrapped in the app shell by TopoloAppRoot.
const routes: AppRoute[] = [
  { path: '/dashboard', ...DashboardRoute },
  { path: '/calendar', ...CalendarRoute },
  { path: '/projects', ...ProjectsRoute },
  { path: '/projects/new', ...NewProjectGalleryRoute },
  { path: '/projects/new/manual', ...NewProjectRoute },
  { path: '/projects/:id', ...ProjectDetailRoute },
  { path: '/projects/:id/dashboard', ...ProjectDashboardRoute },
  { path: '/roadmaps', ...RoadmapsRoute },
  { path: '/shared', ...SharedRoadmapsRoute },
  { path: '/roadmaps/new', ...NewRoadmapRoute },
  { path: '/roadmaps/:id', ...RoadmapDetailRoute },
  { path: '/roadmap-items/:id', ...RoadmapItemDetailRoute },
  { path: '/presentations/:id', ...PresentationDetailRoute },
  { path: '/settings', element: <Navigate to="/settings/api-keys" replace /> },
  { path: '/settings/:section', ...SettingsRoute },
  { path: '/import', ...ImportPageRoute },
  { path: '/export', ...ExportPageRoute },
  { path: '/reports', ...ReportsRoute },
  { path: '/getting-started', ...GettingStartedPageRoute },
];

// Exported for the nav ↔ route consistency test (app-routes.test.tsx).
export const appRoutePaths = routes.map((route) => route.path);

// Exported for the prefetch regression test: a lazy route without `preload`
// silently reintroduces the chunk-skeleton-then-data-skeleton double load.
export const appRoutePreloadCoverage = routes.map((route) => ({
  path: route.path,
  lazy: typeof route.element === 'object' && route.element !== null,
  preloads: typeof route.preload === 'function',
}));

// Public, UNauthenticated guest share-link surfaces. Rendered bare by
// TopoloAppRoot's publicRoutes — no auth gate, no app shell chrome. Each wrapper
// reads its :token param and renders the existing guest component, which loads
// its data through the guest-token API endpoints.
const publicRoutes: AppRoute[] = [
  { path: '/projects/guest/:token', ...GuestProjectViewRoute },
  { path: '/roadmaps/guest/:token', ...GuestRoadmapViewRoute },
  { path: '/roadmap-items/guest/:token', ...GuestRoadmapItemViewRoute },
];

export function App() {
  const { t } = useTranslation();
  return (
    <TopoloAppErrorBoundary appName="Roadmap">
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <TopoloAppRoot
            appName={APP_NAME}
            appId={getAppId()}
            auth={authClient}
            routes={routes}
            publicRoutes={publicRoutes}
            home={HOME_PATH}
            publicLanding={<RoadmapperLanding />}
            loginElement={<RoadmapperLogin />}
            callbackElement={<RoadmapperAuthCallback />}
            shellComponent={RoadmapperShell}
            workspaceResource={{
              storageKey: ROADMAPPER_WORKSPACE_STORAGE_KEY,
              required: true,
              canCreate: true,
              label: t('roadmapper.workspace.label'),
              createLabel: t('roadmapper.workspace.create'),
              onSelect: () => {
                queryClient.clear();
                void getOfflineHandler().clearCache();
              },
              onDelete: async (workspaceId) => { await api.archiveWorkspace(workspaceId); },
            }}
            loadingFallback={
              <TopoloAppLoadingScaffold
                appName={APP_NAME}
                appId={getAppId()}
                subtitle={t('roadmapper.loading.workspace')}
              />
            }
            onAuthCleared={() => {
              queryClient.clear();
              void getOfflineHandler().clearCache();
            }}
            onOrgChange={() => {
              queryClient.clear();
              void getOfflineHandler().clearCache();
            }}
          />
          <ConfirmDialogHost />
        </ToastProvider>
      </QueryClientProvider>
    </TopoloAppErrorBoundary>
  );
}

/** Protected shell chrome — the existing TopoloAppShell + PWA/offline surfaces. */
function RoadmapperShell({ children }: { children: ReactNode }) {
  return (
    <LazyAppShell>
      <WorkspaceRouteBoundary>{children}</WorkspaceRouteBoundary>
      <PWAInstallBanner />
      <OfflineStatus />
    </LazyAppShell>
  );
}

/** `/` — public marketing landing for guests. */
function RoadmapperLanding() {
  // LandingPage (from @topolo-io/app-funnel) still takes appId; feed it the
  // runtime-resolved app id.
  return <SharedLandingPage appId={getAppId()} />;
}

/** `/login` — shared ui-kit LoginPage driven by the app-shell auth context. */
function RoadmapperLogin() {
  const { login, isLoading } = useAuth();
  return (
    <LoginPage
      appId={getAppId()}
      isLoading={isLoading}
      onLogin={async (provider?: string) => {
        await login(provider || 'topolo');
      }}
      onPasswordLogin={async (request: Parameters<typeof authClient.loginWithPassword>[0]) => {
        const result = await authClient.loginWithPassword(request);
        return result.authenticated ? undefined : result;
      }}
    />
  );
}

/** `/auth/callback` — canonical ui-kit SSO handoff (required by the fleet gate). */
function RoadmapperAuthCallback() {
  const navigate = useNavigate();
  const authenticatedHomePath = useAuthenticatedHomePath({ appId: getAppId(), fallbackPath: HOME_PATH });
  return (
    <TopoloAuthCallback
      appId={getAppId()}
      appName={APP_NAME}
      fallbackPath={authenticatedHomePath}
      authClient={authClient}
      navigate={(path, opts) => navigate(path, { replace: opts?.replace })}
    />
  );
}
